import React from 'react';
import { Link } from 'react-router-dom';
import { Cookie, CheckCircle, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const CookiePolicy = () => {
  const cookieTypes = [
    {
      title: 'Essential Cookies',
      description: 'Required for the website to function properly. These cannot be disabled.',
      examples: 'Session management, security'
    },
    {
      title: 'Analytics Cookies',
      description: 'Help us understand how visitors use our website to improve user experience.',
      examples: 'Google Analytics (if used)'
    },
    {
      title: 'Functional Cookies',
      description: 'Remember your preferences and settings.',
      examples: 'Language preference, accessibility settings'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7] py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <Cookie className="w-16 h-16 text-[#4A90E2] mx-auto" />
            <h1 className="heading-hero">Cookie Policy</h1>
            <p className="body-large">
              How we use cookies on our website and how you can control them.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 mb-6">What Are Cookies?</h2>
            <p className="body-medium text-gray-700 mb-12">
              Cookies are small text files placed on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and understanding how you use our site.
            </p>

            {/* Cookie Types */}
            <h2 className="heading-2 mb-8 text-center">Types of Cookies We Use</h2>
            <div className="space-y-6 mb-12">
              {cookieTypes.map((type, index) => (
                <Card key={index} className="p-8 border-0 hover:shadow-lg transition-all">
                  <h3 className="heading-3 mb-3">{type.title}</h3>
                  <p className="body-medium text-gray-700 mb-3">{type.description}</p>
                  <p className="text-sm text-gray-600">
                    <strong>Examples:</strong> {type.examples}
                  </p>
                </Card>
              ))}
            </div>

            {/* Managing Cookies */}
            <div className="bg-[#E8F4FC] rounded-2xl p-8 md:p-12 mb-12">
              <Settings className="w-12 h-12 text-[#4A90E2] mx-auto mb-6" />
              <h2 className="heading-2 mb-6 text-center">Managing Cookies</h2>
              <p className="body-medium text-gray-700 text-center mb-6">
                You can control and/or delete cookies as you wish. You can delete all cookies that are already on your device and you can set most browsers to prevent them from being placed.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <Card className="p-6 border-0 bg-white">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-[#4A90E2] flex-shrink-0 mt-1" size={20} />
                    <div>
                      <h4 className="font-semibold mb-2">Browser Settings</h4>
                      <p className="text-sm text-gray-700">Most browsers allow you to refuse or accept cookies through settings.</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-6 border-0 bg-white">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-[#4A90E2] flex-shrink-0 mt-1" size={20} />
                    <div>
                      <h4 className="font-semibold mb-2">Third-Party Cookies</h4>
                      <p className="text-sm text-gray-700">You can opt-out of third-party cookies through their websites.</p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>

            {/* Policy Updates */}
            <Card className="p-8 border-0">
              <h3 className="heading-3 mb-4">Changes to This Policy</h3>
              <p className="body-medium text-gray-700">
                We may update this Cookie Policy from time to time. Any changes will be posted on this page with an updated revision date.
              </p>
              <p className="text-sm text-gray-600 mt-4">
                <strong>Last updated:</strong> February 2025
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-gradient-to-r from-[#4A90E2] to-[#7B68BE] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="heading-2 text-white">Questions About Our Cookie Policy?</h2>
            <p className="body-large text-white/90">
              Contact us if you have any questions about how we use cookies.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#4A90E2] hover:bg-gray-100">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicy;
